import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Gauge, ArrowLeft, Loader2, BrainCircuit, AlertTriangle, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import Stepper from "@/components/analyse/Stepper";
import UploadStep from "@/components/analyse/UploadStep";
import ReviewStep from "@/components/analyse/ReviewStep";
import CheckStep from "@/components/analyse/CheckStep";
import ResultStep from "@/components/analyse/ResultStep";
import LeadStep from "@/components/analyse/LeadStep";
import { createAnalysis } from "@/lib/api";

export default function Analyse() {
    const [step, setStep] = useState(0);
    const [vehicle, setVehicle] = useState(null);
    const [check, setCheck] = useState(null);
    const [analysis, setAnalysis] = useState(null);
    const [analyzing, setAnalyzing] = useState(false);
    const [analysisError, setAnalysisError] = useState(null);

    useEffect(() => {
        document.title = "Fahrzeug-Analyse · NA Performance PerformanceOS";
        window.scrollTo(0, 0);
    }, [step]);

    const runAnalysis = async (checkData) => {
        const data = checkData || check;
        if (checkData) setCheck(checkData);
        setAnalysisError(null);
        setAnalyzing(true);
        try {
            const res = await createAnalysis({ vehicle, check: data });
            setAnalysis(res);
            setStep(3);
        } catch (e) {
            const detail = e?.response?.data?.detail || "";
            const isBudget = /budget/i.test(detail);
            setAnalysisError(
                isBudget
                    ? "Das KI-Guthaben (Universal Key) ist aktuell aufgebraucht. Bitte lade dein Guthaben unter Profil → Universal Key → Add Balance auf und versuche es dann erneut."
                    : "Die KI-Analyse ist fehlgeschlagen. Bitte versuche es in einem Moment erneut."
            );
            toast.error("KI-Analyse fehlgeschlagen.");
        } finally {
            setAnalyzing(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#0A0A0A]">
            {/* Top bar */}
            <header className="sticky top-0 z-30 glass border-b border-white/10">
                <div className="mx-auto flex h-16 max-w-5xl items-center justify-between px-4 sm:px-6">
                    <Link to="/" className="flex items-center gap-2.5">
                        <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary">
                            <Gauge className="h-5 w-5 text-white" />
                        </div>
                        <div className="leading-none">
                            <div className="font-heading text-base font-extrabold text-white">NA PERFORMANCE</div>
                            <div className="text-[9px] tracking-[0.25em] uppercase text-primary font-semibold">
                                PerformanceOS
                            </div>
                        </div>
                    </Link>
                    <Link
                        to="/"
                        className="flex items-center gap-2 text-sm text-[#A1A1AA] hover:text-white transition-colors"
                    >
                        <ArrowLeft className="h-4 w-4" /> Startseite
                    </Link>
                </div>
            </header>

            <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6 sm:py-14">
                <Stepper current={step} />

                <div className="mt-12">
                    {analyzing ? (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="mx-auto flex max-w-md flex-col items-center py-16 text-center"
                        >
                            <div className="relative flex h-24 w-24 items-center justify-center">
                                <div className="absolute inset-0 animate-ping rounded-full bg-primary/20" />
                                <div className="flex h-20 w-20 items-center justify-center rounded-full border border-primary/30 bg-primary/10">
                                    <BrainCircuit className="h-9 w-9 text-primary" />
                                </div>
                            </div>
                            <h3 className="mt-8 font-heading text-xl font-bold text-white">
                                KI analysiert dein Fahrzeug
                            </h3>
                            <p className="mt-2 text-sm text-[#A1A1AA]">
                                Wir berechnen realistische Optimierungspotenziale für deinen{" "}
                                {vehicle?.marke} {vehicle?.modell}...
                            </p>
                            <Loader2 className="mt-6 h-5 w-5 animate-spin text-primary" />
                        </motion.div>
                    ) : analysisError ? (
                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            data-testid="analysis-error-panel"
                            className="mx-auto flex max-w-md flex-col items-center py-12 text-center"
                        >
                            <div className="flex h-16 w-16 items-center justify-center rounded-full border border-[#F59E0B]/30 bg-[#F59E0B]/10">
                                <AlertTriangle className="h-8 w-8 text-[#F59E0B]" />
                            </div>
                            <h3 className="mt-6 font-heading text-xl font-bold text-white">
                                Analyse fehlgeschlagen
                            </h3>
                            <p className="mt-3 text-sm leading-relaxed text-[#A1A1AA]">{analysisError}</p>
                            <div className="mt-7 flex w-full flex-col gap-3 sm:flex-row sm:justify-center">
                                <button
                                    data-testid="analysis-retry-btn"
                                    onClick={() => runAnalysis()}
                                    className="inline-flex items-center justify-center gap-2 rounded-md bg-primary px-6 py-3.5 text-base font-bold text-white hover:bg-[#FF3B30] transition-colors"
                                >
                                    <RefreshCw className="h-4 w-4" /> Erneut versuchen
                                </button>
                                <button
                                    data-testid="analysis-edit-btn"
                                    onClick={() => setAnalysisError(null)}
                                    className="inline-flex items-center justify-center gap-2 rounded-md border border-border bg-surface-elevated px-6 py-3.5 text-sm font-medium text-white hover:border-primary/50 transition-colors"
                                >
                                    <ArrowLeft className="h-4 w-4" /> Angaben ändern
                                </button>
                            </div>
                        </motion.div>
                    ) : (
                        <>
                            {step === 0 && (
                                <UploadStep
                                    onComplete={(v) => {
                                        setVehicle(v);
                                        setStep(1);
                                    }}
                                    onManual={() => {
                                        setVehicle({});
                                        setStep(1);
                                    }}
                                />
                            )}
                            {step === 1 && (
                                <ReviewStep
                                    vehicle={vehicle || {}}
                                    onComplete={(v) => {
                                        setVehicle(v);
                                        setStep(2);
                                    }}
                                    onBack={() => setStep(0)}
                                />
                            )}
                            {step === 2 && (
                                <CheckStep onComplete={runAnalysis} onBack={() => setStep(1)} />
                            )}
                            {step === 3 && analysis && (
                                <ResultStep
                                    vehicle={vehicle}
                                    result={analysis.result}
                                    onComplete={() => setStep(4)}
                                />
                            )}
                            {step === 4 && (
                                <LeadStep
                                    vehicle={vehicle}
                                    result={analysis?.result}
                                    analysisId={analysis?.analysis_id}
                                    onBack={() => setStep(3)}
                                />
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
